import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "./axiosConfig";
import { useCart } from "./CartContext";

import {
  User,
  MapPin,
  ListOrdered,
  Edit3,
  Trash2,
  Check,
  Tag,
  HelpCircle,
  ChevronDown,
  Lock,
  Heart,
} from "lucide-react";

export default function Compte() {
  const navigate = useNavigate();
  const { clearCart, setCartFromOrder } = useCart();
  const phone = localStorage.getItem("customerPhone");

  const [user, setUser] = useState(null);
  const isGuest = !user;
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);

  // NOUVEL ÉTAT : Mode édition du profil
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  const [ordersCount, setOrdersCount] = useState(0);

  // NOUVEL ÉTAT : Notifications
  const [notification, setNotification] = useState({
    message: null,
    type: "success",
  });

  // -----------------------------
  // NOTIFICATION UTILITY
  // -----------------------------
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification({ message: null, type: "success" });
    }, 3000);
  };

  // -----------------------------
  // PROFIL
  // -----------------------------
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    gender: "",
  });

  const [profileCompleted, setProfileCompleted] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState("");

  // -----------------------------
  // ADRESSES
  // -----------------------------
  const [newAddress, setNewAddress] = useState({
    label: "",
    street: "",
  });

  const [editingAddressId, setEditingAddressId] = useState(null);
  const [editingAddress, setEditingAddress] = useState({
    label: "",
    street: "",
  });

  // -----------------------------
  // CHARGEMENT INITIAL
  // -----------------------------
  useEffect(() => {
    if (!phone) {
      navigate("/login-client");
      return;
    }

    Promise.allSettled([
      api.get(`/users/client/${phone}`),
      api.get(`/orders/client/${phone}`),
      api.get(`/orders/client/${phone}/count`),
    ])
      .then(([userRes, ordersRes, countRes]) => {
        if (userRes.status === "fulfilled") {
          const u = userRes.value.data.user;
          setUser(u);
          setAddresses(u.addresses || []);

          const currentProfile = {
            firstName: u.firstName || "",
            lastName: u.lastName || "",
            gender: u.gender || "",
          };

          setProfile(currentProfile);
          setProfileCompleted(
            !!(
              currentProfile.firstName &&
              currentProfile.lastName &&
              currentProfile.gender
            )
          );
        }

        if (ordersRes.status === "fulfilled") {
          setOrders(ordersRes.value.data.orders || []);
        }

        if (countRes.status === "fulfilled") {
          setOrdersCount(countRes.value.data.count || 0);
        }
      })
      .finally(() => setLoading(false));
  }, [phone, navigate]);                                                                                           
  // -----------------------------
  // PROFIL — MISE À JOUR
  // -----------------------------
  const saveProfile = async () => {
    setProfileError("");

    if (!profile.firstName || !profile.lastName || !profile.gender) {
      setProfileError("Merci de compléter tous les champs requis");
      return;
    }

    try {
      setSavingProfile(true);

      const res = await api.put(`/users/client/${phone}`, {
        firstName: profile.firstName.trim(),
        lastName: profile.lastName.trim(),
        gender: profile.gender,
      });

      setUser(res.data.user);
      setProfileCompleted(true);
      setIsEditingProfile(false);
      showNotification("✅ Profil mis à jour", "success");
    } catch {
      setProfileError("Erreur lors de la sauvegarde");
      showNotification("❌ Erreur lors de la mise à jour du profil", "error");
    } finally {
      setSavingProfile(false);
    }
  };

  const startEditProfile = () => {
    if (isGuest) {
      showNotification(
        "Pour compléter vos informations personnelles, veuillez d’abord créer un compte. Déconnectez-vous, puis inscrivez-vous pour accéder à toutes les fonctionnalités.",
        "warning"
      );
      return;
    }

    setProfileError("");
    setIsEditingProfile(true);
  };

  const cancelEditProfile = () => {
    setProfile({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      gender: user.gender || "",
    });
    setProfileError("");
    setIsEditingProfile(false);
  };

// -----------------------------
// ADRESSES — CRUD
// -----------------------------
const addAddress = async () => {
  if (isGuest) {
    showNotification(
      "Pour ajouter une adresse, veuillez d’abord créer un compte. Déconnectez-vous, puis inscrivez-vous pour accéder à toutes les fonctionnalités.",
      "warning"
    );
    return;
  }

  if (!newAddress.street.trim()) return;

  try {
    const res = await api.post(
      `/users/client/${phone}/address`,
      newAddress
    );
    setAddresses(res.data.addresses);
    setNewAddress({ label: "", street: "" });
    showNotification("✅ Nouvelle adresse ajoutée", "success");
  } catch {
    showNotification("❌ Erreur lors de l'ajout de l'adresse", "error");
  }
};

const setDefaultAddress = async (id) => {
  if (isGuest) {
    showNotification(
      "Veuillez créer un compte pour gérer vos adresses.",
      "warning"
    );
    return;
  }

  try {
    const res = await api.put(
      `/users/client/${phone}/address/${id}`,
      { isDefault: true }
    );
    setAddresses(res.data.addresses);
    showNotification("✅ Adresse par défaut définie", "success");
  } catch {
    showNotification(
      "❌ Erreur lors de la définition par défaut",
      "error"
    );
  }
};

const deleteAddress = async (id) => {
  if (isGuest) {
    showNotification(
      "Veuillez créer un compte pour gérer vos adresses.",
      "warning"
    );
    return;
  }

  if (!window.confirm("Voulez-vous vraiment supprimer cette adresse ?"))
    return;

  try {
    const res = await api.delete(
      `/users/client/${phone}/address/${id}`
    );
    setAddresses(res.data.addresses);
    showNotification("🗑️ Adresse supprimée", "warning");
  } catch {
    showNotification(
      "❌ Erreur lors de la suppression de l'adresse",
      "error"
    );
  }
};

const saveEditedAddress = async (id) => {
  if (isGuest) {
    showNotification(
      "Veuillez créer un compte pour gérer vos adresses.",
      "warning"
    );
    return;
  }

  try {
    const res = await api.put(
      `/users/client/${phone}/address/${id}`,
      {
        label: editingAddress.label,
        street: editingAddress.street,
      }
    );
    setAddresses(res.data.addresses);
    setEditingAddressId(null);
    showNotification("✅ Adresse modifiée", "success");
  } catch {
    showNotification(
      "❌ Erreur lors de la modification de l'adresse",
      "error"
    );
  }
};
  const deleteAccount = async () => {
  const confirmFirst = window.confirm(
    "🚨 ATTENTION : Voulez-vous vraiment supprimer votre compte ? Cette action est irréversible et supprimera toutes vos données (adresses, favoris, historique)."
  );

  if (!confirmFirst) return;

  const confirmSecond = window.confirm(
    "Dernière vérification : Êtes-vous ABSOLUMENT sûr ? Vous perdrez l'accès à vos coupons et commandes en cours."
  );

  if (!confirmSecond) return;

  try {
    await api.delete(`/users/client/${phone}`);
    showNotification("👋 Votre compte a été supprimé.", "success");
    
    // Nettoyage et redirection
    localStorage.removeItem("customerPhone");
    navigate("/");
  } catch (err) {
    console.error(err);
    showNotification("❌ Erreur lors de la suppression du compte", "error");
  }
};
  // -----------------------------
  // COMMANDES
  // -----------------------------
  const handleRecommencer = async (order) => {
    if (!order?.items?.length) return;
    clearCart();
    await setCartFromOrder(order.items, order.merchantId);
    navigate("/panier");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-500 bg-gray-50">
        Chargement du compte...
      </div>
    );
  }

  const recentOrders = orders.slice(0, 3);
  const defaultAddress = addresses.find((addr) => addr.isDefault);
  const isFidele = ordersCount >= 10;
  
  const InputFieldStyle =
    "w-full py-3 px-4 pl-12 border border-gray-200 rounded-xl bg-gray-50 text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[#f34141] transition-colors shadow-inner";

  const InputContainerStyle =
    "relative flex items-center focus-within:ring-2 focus-within:ring-red-100 focus-within:ring-offset-1 focus-within:rounded-xl";

  const NotificationStyles = {
    success: "bg-green-500",
    error: "bg-red-500",
    warning: "bg-orange-500",
  };  return (
    <div className="min-h-screen px-4 pb-32 space-y-6 font-sans pt-28 bg-gray-50">

      {/* NOTIFICATION (Toast) */}
      {notification.message && (
        <div
          className={`fixed top-24 left-0 right-0 z-[9999] p-4 transition-all duration-300 ${NotificationStyles[notification.type]}`}
        >
          <div className="max-w-md mx-auto font-semibold text-center text-white rounded-lg shadow-xl">
            {notification.message}
          </div>
        </div>
      )}

      {/* EN-TÊTE COMPTE */}
      <div className="p-6 space-y-4 bg-white shadow-md rounded-3xl">
        <div className="flex items-center space-x-4">
          <div className="flex items-center justify-center w-16 h-16 bg-[#f34141] text-white text-3xl font-bold rounded-full shadow-lg">
            {user?.firstName ? (
              user.firstName[0].toUpperCase()
            ) : (
              <User size={32} />
            )}
          </div>

          <div>
            <h1 className="flex items-center gap-2 text-xl font-extrabold text-gray-800">
              Salut, {user?.firstName || "Client"} !
              {isFidele && (
                <span className="text-xs font-bold text-white bg-green-500 px-2 py-0.5 rounded-full shadow-md">
                  Client Fidèle
                </span>
              )}
            </h1>
            <p className="text-sm font-medium text-gray-500">
              Client – {phone}
            </p>
          </div>
        </div>

        {/* STATS */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="w-1/2 text-center">
            <p className="text-xl font-bold text-[#f34141]">
              {ordersCount}
            </p>
            <p className="text-xs font-medium text-gray-500">
              Commandes Totales
            </p>
          </div>

          <div className="w-[1px] h-10 bg-gray-200" />

          <div className="w-1/2 text-center">
            <p className="px-1 text-sm font-bold text-gray-800 truncate">
              {defaultAddress ? defaultAddress.label : "Non définie"}
            </p>
            <p className="text-xs font-medium text-gray-500">
              Adresse par défaut
            </p>
          </div>
        </div>
      </div>

      {/* MENU ACTIONS RAPIDES */}
      <div className="grid grid-cols-4 gap-3 p-4 bg-white shadow-sm rounded-2xl">
        <button
          onClick={() => navigate("/commandes")}
          className="flex flex-col items-center p-2 transition rounded-xl hover:bg-gray-50"
        >
          <ListOrdered className="w-6 h-6 text-[#f34141]" />
          <span className="mt-1 text-xs font-semibold text-gray-700">
            Commandes
          </span>
        </button>

        <button
          onClick={() => navigate("/client/coupons")}
          className="flex flex-col items-center p-2 transition rounded-xl hover:bg-gray-50"
        >
          <Tag className="w-6 h-6 text-orange-400" />
          <span className="mt-1 text-xs font-semibold text-gray-700">
            Coupons
          </span>
        </button>

        {/* ❤️ MES FAVORIS */}
        <button
          onClick={() => navigate("/client/favoris")}
          className="flex flex-col items-center p-2 transition rounded-xl hover:bg-gray-50"
        >
          <Heart className="w-6 h-6 text-red-500" />
          <span className="mt-1 text-xs font-semibold text-gray-700">
            Mes favoris
          </span>
        </button>

        <button
          onClick={() => navigate("/aide-faq")}
          className="flex flex-col items-center p-2 transition rounded-xl hover:bg-gray-50"
        >
          <HelpCircle className="w-6 h-6 text-gray-500" />
          <span className="mt-1 text-xs font-semibold text-gray-700">
            Aide / FAQ
          </span>
        </button>
      </div>      {/* PROFIL */}
      <div className="p-6 bg-white shadow-sm rounded-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold text-gray-800">
            Vos informations personnelles
          </h2>

          {/* ACTION EDIT / SAVE */}
          {isEditingProfile ? (
            <button
              onClick={saveProfile}
              disabled={savingProfile}
              className="flex items-center gap-1 text-green-600 hover:text-green-700 disabled:opacity-50"
              title="Enregistrer"
            >
              {savingProfile ? "…" : <Check className="w-6 h-6" />}
            </button>
          ) : (
            <button
              onClick={startEditProfile}
              className="text-[#f34141] hover:text-red-600"
              title="Modifier"
            >
              <Edit3 className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* MODE LECTURE */}
        {!isEditingProfile ? (
          <div className="space-y-3 text-gray-700">
            <p>
              <span className="font-semibold text-gray-500">Prénom :</span>{" "}
              {user?.firstName || "N/A"}
            </p>
            <p>
              <span className="font-semibold text-gray-500">Nom :</span>{" "}
              {user?.lastName || "N/A"}
            </p>
            <p>
              <span className="font-semibold text-gray-500">Sexe :</span>{" "}
              {user?.gender || "N/A"}
            </p>
          </div>
        ) : (
          /* MODE ÉDITION */
          <div className="space-y-4">
            <div className={InputContainerStyle}>
              <User className="absolute w-5 h-5 text-gray-400 left-4" />
              <input
                placeholder="Prénom"
                value={profile.firstName}
                onChange={(e) =>
                  setProfile({ ...profile, firstName: e.target.value })
                }
                className={InputFieldStyle}
              />
            </div>

            <div className={InputContainerStyle}>
              <User className="absolute w-5 h-5 text-gray-400 left-4" />
              <input
                placeholder="Nom"
                value={profile.lastName}
                onChange={(e) =>
                  setProfile({ ...profile, lastName: e.target.value })
                }
                className={InputFieldStyle}
              />
            </div>

            <div className={InputContainerStyle}>
              <User className="absolute w-5 h-5 text-gray-400 left-4" />
              <ChevronDown className="absolute w-5 h-5 text-gray-400 pointer-events-none right-4" />
              <select
                value={profile.gender}
                onChange={(e) =>
                  setProfile({ ...profile, gender: e.target.value })
                }
                className={`${InputFieldStyle} appearance-none`}
              >
                <option value="">Sexe</option>
                <option value="Masculin">Masculin</option>
                <option value="Féminin">Féminin</option>
              </select>
            </div>

            {profileError && (
              <p className="text-sm font-medium text-red-500">
                {profileError}
              </p>
            )}

            <button
              onClick={cancelEditProfile}
              className="w-full py-3 font-bold text-gray-600 transition bg-gray-200 rounded-xl hover:bg-gray-300"
            >
              Annuler la modification
            </button>
          </div>
        )}
      </div>      {/* ADRESSES */}
      <div className="p-6 bg-white shadow-sm rounded-2xl">
        <h2 className="mb-6 text-xl font-bold text-gray-800">
          Mes adresses
        </h2>

        {/* LISTE DES ADRESSES */}
        <div className="mb-6 space-y-6">
          {addresses.map((addr) => (
            <div
              key={addr._id}
              className="pb-4 border-b border-gray-100 last:border-none"
            >
              {editingAddressId === addr._id ? (
                /* MODE ÉDITION ADRESSE */
                <div className="p-4 space-y-3 shadow-inner bg-gray-50 rounded-xl">
                  <input
                    value={editingAddress.label}
                    placeholder="Label (ex: Maison)"
                    onChange={(e) =>
                      setEditingAddress({
                        ...editingAddress,
                        label: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border rounded-xl bg-white focus:outline-none focus:ring-1 focus:ring-[#f34141]"
                  />

                  <input
                    value={editingAddress.street}
                    placeholder="Adresse"
                    onChange={(e) =>
                      setEditingAddress({
                        ...editingAddress,
                        street: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border rounded-xl bg-white focus:outline-none focus:ring-1 focus:ring-[#f34141]"
                  />

                  <div className="flex justify-end gap-4 pt-2">
                    <button
                      onClick={() => setEditingAddressId(null)}
                      className="font-medium text-gray-500 hover:text-gray-700"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={() => saveEditedAddress(addr._id)}
                      className="flex items-center gap-1 font-bold text-green-600"
                    >
                      <Check className="w-4 h-4" />
                      Enregistrer
                    </button>
                  </div>
                </div>
              ) : (
                /* MODE LECTURE ADRESSE */
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-gray-800">
                        {addr.label || "Adresse"}
                      </p>

                      {addr.isDefault && (
                        <span className="px-2 py-0.5 text-xs font-bold text-green-700 bg-green-100 rounded-full">
                          Par défaut
                        </span>
                      )}
                    </div>

                    <p className="mt-1 text-sm text-gray-500">
                      {addr.street}
                    </p>

                    {!addr.isDefault && (
                      <button
                        onClick={() => setDefaultAddress(addr._id)}
                        className="mt-2 text-xs font-medium text-[#f34141] hover:underline"
                      >
                        Définir par défaut
                      </button>
                    )}
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button
                      onClick={() => {
                        setEditingAddressId(addr._id);
                        setEditingAddress({
                          label: addr.label || "",
                          street: addr.street || "",
                        });
                      }}
                      className="p-1 text-gray-600 hover:text-[#f34141] transition"
                      title="Modifier"
                    >
                      <Edit3 className="w-5 h-5" />
                    </button>

                    <button
                      onClick={() => deleteAddress(addr._id)}
                      className="p-1 text-gray-400 transition hover:text-red-500"
                      title="Supprimer"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* AJOUT NOUVELLE ADRESSE */}
        <div className="pt-2 space-y-4">
          <h3 className="text-lg font-semibold text-gray-700">
            Ajouter une nouvelle adresse
          </h3>

          <div className={InputContainerStyle}>
            <Tag className="absolute w-5 h-5 text-gray-400 left-4" />
            <input
              placeholder="Label (Maison, Travail...)"
              value={newAddress.label}
              onChange={(e) =>
                setNewAddress({ ...newAddress, label: e.target.value })
              }
              className={InputFieldStyle}
            />
          </div>

          <div className={InputContainerStyle}>
            <MapPin className="absolute w-5 h-5 text-gray-400 left-4" />
            <input
              placeholder="Adresse complète"
              value={newAddress.street}
              onChange={(e) =>
                setNewAddress({ ...newAddress, street: e.target.value })
              }
              className={InputFieldStyle}
            />
          </div>

          <button
            onClick={addAddress}
            className="w-full py-3.5 font-bold text-white bg-[#f34141] rounded-xl shadow-lg hover:bg-red-600 transition"
          >
            Ajouter l’adresse
          </button>
        </div>
      </div>      {/* COMMANDES RÉCENTES */}
      <div className="p-6 bg-white shadow-sm rounded-2xl">
        <h2 className="mb-6 text-xl font-bold text-gray-800">
          Commandes récentes
        </h2>

        {recentOrders.length === 0 ? (
          <p className="text-sm text-gray-400">
            Aucune commande récente.
          </p>
        ) : (
          recentOrders.map((order) => (
            <div
              key={order._id}
              className="flex items-center justify-between py-3 border-b border-gray-100 last:border-none"
            >
              <div>
                <p className="font-bold text-gray-800">
                  {order.orderNumber}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  {new Date(order.createdAt).toLocaleString("fr-FR")}
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => handleRecommencer(order)}
                  className="text-sm font-medium text-gray-500 hover:underline"
                >
                  Recommencer
                </button>

                <button
                  onClick={() =>
                    navigate(`/commandes/${order.orderNumber}`)
                  }
                  className="text-sm font-bold text-[#f34141] hover:underline"
                >
                  Détails
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ACTIONS COMPLÉMENTAIRES */}
      <div className="pt-2 space-y-4">
        <button
          onClick={() => navigate("/commandes")}
          className="w-full py-3.5 font-bold text-white bg-[#f34141] rounded-xl shadow-lg hover:bg-red-600 transition-colors"
        >
          Voir toutes mes commandes
        </button>

        {/* CHANGER MOT DE PASSE */}
        <button
          onClick={() => navigate("/client/changer-mot-de-passe")}
          className="w-full flex items-center justify-center gap-2 py-3.5 font-bold text-gray-700 bg-white border border-gray-200 rounded-xl shadow-md hover:bg-gray-50 transition"
        >
          <Lock className="w-5 h-5 text-gray-500" />
          Changer mon mot de passe
        </button>

        {/* DÉCONNEXION */}
        <div className="text-center">
          <button
            onClick={() => {
              localStorage.removeItem("customerPhone");
              navigate("/");
            }}
            className="py-2 font-bold text-red-600 underline decoration-2 hover:text-red-700"
          >
            Déconnexion
          </button>
          {/* BOUTON SUPPRIMER LE COMPTE (Exigence Apple) */}
          <button
            onClick={deleteAccount}
            className="text-xs font-medium text-red-400 transition hover:text-red-600"
          >
            Supprimer définitivement mon compte
          </button>
        </div>
      </div>
    </div>
  );
}

